// File upload utility for web mode
// Uploads files to the server and returns file paths

export interface UploadedFile {
  name: string;
  mimeType: string;
  filePath: string;
  size: number;
}

export interface UploadResult {
  files: UploadedFile[];
  rejected?: string[];
}

const ALLOWED_MIME_PREFIXES = [
  'image/', 'audio/', 'video/',
  'text/', 'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel', 'application/msword',
  'application/json', 'application/xml',
];

export const ALLOWED_EXTENSIONS = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg', '.ico',
  '.mp3', '.wav', '.ogg', '.m4a', '.webm', '.flac', '.aac', '.wma',
  '.mp4', '.avi', '.mov', '.mkv', '.wmv', '.flv',
  '.txt', '.md', '.csv', '.tsv', '.log', '.rtf',
  '.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx',
  '.json', '.xml', '.yaml', '.yml',
  '.drawio', '.vsdx', '.vsd',
]);

export function isAllowedFile(file: File): boolean {
  const ext = file.name.includes('.')
    ? '.' + file.name.split('.').pop()!.toLowerCase()
    : '';
  const mimeOk = ALLOWED_MIME_PREFIXES.some(prefix => file.type.startsWith(prefix));
  const extOk = ALLOWED_EXTENSIONS.has(ext);
  return mimeOk || extOk;
}

function getServerUrl(): string | null {
  // Try to derive from the WebSocket URL used by the transport
  const wsUrl = localStorage.getItem('cowork_server_url')
    || localStorage.getItem('ws_url')
    || import.meta.env.VITE_WS_URL;
  if (wsUrl) {
    const httpUrl = wsUrl.replace(/\/ws$/, '');
    const httpProto = httpUrl.startsWith('wss') ? 'https' : 'http';
    return httpProto + '://' + httpUrl.replace(/^wss?:\/\//, '');
  }
  // Fallback: derive from window.location (same as web-entry.tsx default)
  const apiUrl = import.meta.env.VITE_API_URL || window.location.origin;
  if (apiUrl) {
    return apiUrl;
  }
  return null;
}

function getAuthToken(): string | null {
  return localStorage.getItem('auth_token') || null;
}

export async function uploadFiles(files: File[]): Promise<UploadResult> {
  return uploadFilesWeb(files);
}



async function uploadFilesWeb(files: File[]): Promise<UploadResult> {
  const rejected: string[] = [];
  const accepted: File[] = [];
  for (const file of files) {
    if (!isAllowedFile(file)) {
      rejected.push(`${file.name}: unsupported file type`);
      continue;
    }
    accepted.push(file);
  }
  if (accepted.length === 0) {
    return { files: [], rejected };
  }

  const serverUrl = getServerUrl();
  if (!serverUrl) {
    throw new Error('No server URL configured for file upload');
  }

  const formData = new FormData();
  for (const file of accepted) {
    formData.append('files', file);
  }

  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${serverUrl}/api/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `Upload failed: ${response.status}` }));
    throw new Error(errorData.error || `Upload failed: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Upload failed');
  }

  const serverRejected = data.rejected as string[] | undefined;
  const allRejected = [...rejected, ...(serverRejected || [])];

  return { files: data.files as UploadedFile[], rejected: allRejected.length > 0 ? allRejected : undefined };
}

export async function uploadBlob(blob: Blob, name: string, mimeType: string): Promise<UploadedFile> {
  return uploadBlobWeb(blob, name, mimeType);
}


async function uploadBlobWeb(blob: Blob, name: string, mimeType: string): Promise<UploadedFile> {
  const serverUrl = getServerUrl();
  if (!serverUrl) {
    throw new Error('No server URL configured for file upload');
  }

  const file = new File([blob], name, { type: mimeType });
  const formData = new FormData();
  formData.append('files', file);

  const token = getAuthToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${serverUrl}/api/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: `Upload failed: ${response.status}` }));
    throw new Error(errorData.error || `Upload failed: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success) {
    throw new Error(data.error || 'Upload failed');
  }

  return data.files[0] as UploadedFile;
}

export function getFileUrl(filePath: string): string | null {
  const serverUrl = getServerUrl();
  if (!serverUrl) return null;
  const filename = filePath.split(/[/\\]/).pop();
  if (!filename) return null;
  return `${serverUrl}/api/files/${filename}`;
}

export async function getFileDataUrl(filePath: string): Promise<string | null> {
  const url = getFileUrl(filePath);
  if (!url) return null;
  try {
    const headers: Record<string, string> = {};
    const token = getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, { headers });
    if (!response.ok) return null;
    const blob = await response.blob();
    return blobToDataUrlSync(blob);
  } catch {
    return null;
  }
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('Failed to read blob'));
      }
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read blob'));
    reader.readAsDataURL(blob);
  });
}

function blobToDataUrlSync(blob: Blob): Promise<string> {
  return blobToDataUrl(blob);
}